import React from "react";

import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { Connection } from "src/api/connectApi/types";
import { FieldErrors } from "src/api/types";
import { Icon } from "src/base-components/Icon";
import { VerbDropDown } from "src/customConnectionNode/editorComponents/VerbDropDown";
import { truncateUrl } from "src/customConnectionNode/truncateUrl";
import { CustomConnectionForm } from "src/customConnectionNode/types";
import { Tooltip } from "src/design-system/Tooltip";

const focusClassName =
  "focus-within:outline-none focus-within:ring-1 focus-within:ring-indigo-400";
const maxBaseUrlChars = 24;
type Props = {
  immutable: boolean;
  connection: Connection;
  runFieldErrors: FieldErrors | undefined;
};

export const VerbPathSelector: React.FC<Props> = ({
  immutable,
  connection,
  runFieldErrors,
}) => {
  const formProps = useFormContext<CustomConnectionForm>();
  const pathFieldError = runFieldErrors?.[formProps.getValues("path.id")];

  const containerStyles =
    "relative flex bg-white items-center border rounded-lg border-gray-200 mb-6";

  const inputStyles = twJoin(
    "h-10 flex-1 rounded-lg py-1",
    pathFieldError ? "pr-7" : "pr-2",
    "font-code-12",
    "bg-white text-gray-800",
    "focus:outline-none",
  );

  const prefixProps = twJoin(
    "pl-4",
    "max-w-48 flex-none",
    "font-code-12",
    "text-gray-400",
  );

  const truncatedBaseURL = truncateUrl(
    connection.configuration?.base_url,
    maxBaseUrlChars,
  );
  const sandboxAvailable =
    connection.enable_non_prod_configs &&
    Boolean(connection.non_prod_env_configs?.["sandbox"]);

  const TooltipBody = (
    <div className="w-full">
      <div className="w-full font-inter-normal-12px text-gray-500">
        Production environment base URL
      </div>
      <div className="mb-1 w-full break-all font-inter-semibold-12px text-white-800">
        {connection.configuration?.base_url}
      </div>

      {sandboxAvailable && (
        <>
          <div className="w-full font-inter-normal-12px text-gray-500">
            Sandbox environment base URL
          </div>
          <div className="w-full break-all font-inter-semibold-12px text-white-800">
            {connection.non_prod_env_configs?.["sandbox"]?.["base_url"]}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={containerStyles}>
      <div className="font-code-12">
        <VerbDropDown immutable={immutable} />
      </div>
      <Tooltip
        body={pathFieldError}
        disabled={!pathFieldError}
        placement="top"
        title="Invalid input"
        asChild
      >
        <div
          className={twJoin(
            "z-1 ml-[1px] flex w-full items-center rounded-r-lg bg-white",
            pathFieldError && "ring-1 ring-red-500",
            focusClassName,
          )}
        >
          <Tooltip
            body={TooltipBody}
            disabled={
              !(
                sandboxAvailable ||
                truncatedBaseURL !== connection.configuration?.base_url
              )
            }
            placement="bottom"
            asChild
          >
            <span className={prefixProps}>{truncatedBaseURL}</span>
          </Tooltip>
          <input
            autoComplete="off"
            className={inputStyles}
            data-loc="custom-connection-path-expression"
            {...formProps.register("path.expression")}
            disabled={immutable}
          ></input>
          {pathFieldError && (
            <div className="absolute right-3">
              <Icon
                color="text-red-400"
                cursorType="text"
                icon={faWarning}
                size="xs"
              />
            </div>
          )}
        </div>
      </Tooltip>
    </div>
  );
};
